<template>
  <v-main v-resize="onResize" class="pt-0">
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card class="d-flex pa-1 bg" outlined>
            <div class="mr-auto d-flex">
              <date-time-range-picker-neo v-model="datetimes"></date-time-range-picker-neo>
              <v-divider class="ml-3 mr-2" vertical></v-divider>
              <v-select v-model="type" :items="types" class="mx-1" return-object solo dense hide-details></v-select>
              <v-divider class="ml-3 mr-2" vertical></v-divider>
              <v-checkbox v-model="cbo" :label="this.$t('hist.cbo.only')" dense hide-details></v-checkbox>
              <v-divider class="ml-3 mr-2" vertical></v-divider>
              <!-- eslint-disable-next-line prettier/prettier -->
              <v-select v-model="selectedTaskTypes" :items="taskTypes" item-text="nm" item-value="id"
                :label="$t('motlet.taskType')" multiple outlined dense :hide-details="true"
                :menu-props="{ offsetY: true }" :style="{ width: '430px', marginLeft: '6px' }">
                <template v-slot:prepend-item>
                  <v-list-item ripple @mousedown.prevent @click="toggleTaskType">
                    <v-list-item-action>
                      <v-icon>{{ iconTaskType }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t('common.select.all') }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>
              <v-btn @click="fetch" color="primary" class="ml-2" x-small>
                <v-icon small>mdi-loupe</v-icon>
              </v-btn>
            </div>
            <div>
              <download-button :loading="downloading" :disabled="disableDownload" @download="download"></download-button>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row :style="{ height: height, overflow: 'auto' }">
        <v-col cols="3" v-for="m in modules" :key="m">
          <h3 class="top-title">
            <v-icon>mdi-circle-medium</v-icon>
            {{ $t('topexe.ttl.main', { cboStr: cboString, module: m }) }}
          </h3>
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-data-table
            :headers="headers" :items="items[m]" item-key="rank"
            :items-per-page="paging[m].rowsPerPage" :loading="loading[m]"
            :height="480" class="elevation-1 content"
            fixed-header dense hide-default-footer single-select
            @click:row="highlight" @contextmenu:row="showContextMenu"
          >
            <template #item.tcrp="{ item }">
              <v-tooltip content-class="tooltip" right>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ item.tcrp }}</span>
                </template>
                <span small>{{ item.description }}</span>
              </v-tooltip>
            </template>
            <template #item.cnt="{ value }">{{ value | comma }}</template>
            <template #footer>
              <pagination :paging.sync="paging[m]" :length="length[m]" :disabled="loading[m]"></pagination>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-menu v-model="showMenu" :position-x="x" :position-y="y" z-index="10" absolute offset-y>
        <v-list>
          <v-list-item v-for="(menu, index) in menus" :key="index" @click="openViewer(menu.id)" link>
            <v-list-item-title>{{ menu.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-container>
  </v-main>
</template>

<script>
import { isEmpty, merge } from 'lodash';
import { mapState } from 'vuex';
import { startOfDay } from 'date-fns';
import { api } from '@/api/common/instances';
import { TASK_TYPES } from '@/constants';

export default {
  data() {
    return {
      /* search condition */
      datetimes: [startOfDay(new Date()), new Date()],
      types: [
        { text: this.$t('toplst.type.tcode'), value: 'T' },
        { text: this.$t('toplst.type.report'), value: 'R' },
      ],
      type: { text: this.$t('toplst.type.tcode'), value: 'T' },
      cbo: true,
      taskTypes: TASK_TYPES(),
      selectedTaskTypes: ['1'],
      report: '',
      tcode: '',
      /* download */
      downloading: false,
      /* table */
      modules: [],
      items: {},
      length: {},
      paging: {},
      oldPaging: {},
      loading: {},
      height: '',
      headers: [
        { text: this.$t('toplst.ttl.rank'), value: 'rank', sortable: false, width: '50px', align: 'end' },
        { text: this.$t('toplst.ttl.tcode'), value: 'tcrp', sortable: false, align: 'start' },
        { text: this.$t('toplst.ttl.cnt'), value: 'cnt', sortable: false, width: '100px', align: 'end' },
      ],
      /* context menu */
      showMenu: false,
      menus: [{ id: 'PGM', title: this.$t('toplst.ttl.ctxt.menu.pgm'), disabled: false }],
      x: 0,
      y: 0,
      ctxtClickedItem: {},
      // API
      url: null,
      params: null,
    };
  },
  computed: {
    ...mapState('common/system', ['system']),
    cboString() {
      return this.cbo ? 'CBO' : '';
    },
    disableDownload() {
      const positiveItem = Object.keys(this.length).findIndex(key => this.length[key] > 0);
      return positiveItem < 0 ? true : false;
    },
    likesAllTaskType() {
      return this.selectedTaskTypes.length === this.taskTypes.length;
    },
    likesSomeTaskType() {
      return this.selectedTaskTypes.length > 0;
    },
    iconTaskType() {
      if (this.likesAllTaskType) return 'mdi-close-box';
      if (this.likesSomeTaskType) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
  },
  watch: {
    $route: {
      handler: 'onChangeRouteHandler',
    },
    /**
     * paging의 내부 속성이 동적으로 생성되며 paging의 deep watch를 해야하는데
     * deep watch를 하게 되면 watcher()에서 value와 oldValue가 동일한 객체를 참조하게 된다.
     * 따라서, oldPaging에 paging을 merge() 하여 따로 보관해두고 변경 여부를 체크한다.
     */
    paging: {
      deep: true,
      handler(val) {
        Object.keys(val).forEach(key => {
          if (val[key].page != this.oldPaging[key].page || val[key].rowsPerPage != this.oldPaging[key].rowsPerPage) {
            this.oldPaging = merge({}, val);
            this.fetchInner(key);
          }
        });
      },
    },
    'type.value': {
      handler(val) {
        this.resetData();
        if (val == 'R') {
          this.headers.splice(1, 1);
          this.headers.splice(1, -1, { text: this.$t('toplst.ttl.rpt'), value: 'tcrp', sortable: false, align: 'start' });
        } else if (val == 'T') {
          this.headers.splice(1, 1);
          this.headers.splice(1, -1, { text: this.$t('toplst.ttl.tcode'), value: 'tcrp', sortable: false, align: 'start' });
        }
      },
    },
    cbo: {
      handler: 'resetData',
    },
    selectedTaskTypes: {
      handler: 'resetData',
    },
  },
  methods: {
    resetData() {
      this.modules.forEach(m => {
        this.$set(this.items, m, []);
        this.$set(this.length, m, 0);
        this.length = merge({}, this.length);
      });
    },
    toggleTaskType() {
      this.$nextTick(() => {
        if (this.likesAllTaskType) {
          this.selectedTaskTypes = [];
        } else {
          this.selectedTaskTypes = this.taskTypes.map(taskType => taskType.id);
        }
      });
    },
    onChangeRouteHandler() {
      this.fetch();
    },
    makeFetchParams(urlSuffix) {
      const params = {
        srchBgnYmdhms: this.datetimes[0].getTime(),
        srchEndYmdhms: this.datetimes[1].getTime(),
        sstmId: this.system.id,
      };
      params.type = this.type.value;
      params.exOwlens = true;
      params.exSystem = true;
      params.taskTypes = this.selectedTaskTypes.toString();
      params.cboOnly = this.cbo;

      this.url = `/statistics/top-execution-list${urlSuffix}`;
      this.params = params;
    },
    async fetch() {
      this.makeFetchParams('');
      // JavaScript Array의 forEach()는 await을 하지 않으므로 다음과 같이 작성하여야 한다.
      for (const m of this.modules) {
        await this.fetchInner(m);
      }
    },
    async fetchInner(module) {
      this.$set(this.loading, module, true);
      const params = this.params;
      params.module = module;
      params.page = this.paging[module].page;
      params.limit = this.paging[module].rowsPerPage;
      const {
        data: { items, pagination },
      } = await api.get(this.url, { params });
      this.$set(this.items, module, items);
      this.$set(this.length, module, pagination.totalCount);
      this.$set(this.loading, module, false);
    },
    download() {
      this.downloading = true;
      this.makeFetchParams('-excel');
      const params = this.params;
      params.modules = this.modules.join(',');
      // eslint-disable-next-line prettier/prettier
      api.get(this.url, {params, responseType: 'blob'})
        .then(({ data, headers }) => {
        let filename = '';
        const disposition = headers['content-disposition'];
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
          if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
        }
        const type = headers['content-type'];
        const blob = new Blob([data], { type: type });
        if (typeof window.navigator.msSaveBlob !== 'undefined') {
          window.navigator.msSaveBlob(blob, filename);
        } else {
          const URL = window.URL || window.webkitURL;
          const downloadUrl = URL.createObjectURL(blob);
          if (filename) {
            const a = document.createElement('a');
            if (typeof a.download === 'undefined') {
              window.location = downloadUrl;
            } else {
              a.href = downloadUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
            }
          } else {
            window.location = downloadUrl;
          }
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 100); // cleanup
        }
        this.downloading = false;
      });
    },
    highlight(item, { isSelected, select }) {
      select(!isSelected);
    },
    /**
     * 테이블 row 우클릭 시 context menu 보여주기
     */
    showContextMenu(e, { item }) {
      e.preventDefault();
      this.ctxtClickedItem = item;
      this.x = e.clientX;
      this.y = e.clientY;
      this.showMenu = true;
    },
    /**
     * Context menu 선택 시 해당 화면 띄우기
     */
    openViewer(id) {
      let url, params;

      if (id === 'PGM') {
        params = {
          typ: 'tcrp',
          sdt: this.datetimes[0].getTime(),
          edt: this.datetimes[1].getTime(),
        };
        if (this.type.value == 'R') params.rpt = this.ctxtClickedItem.tcrp;
        if (this.type.value == 'T') params.tcod = this.ctxtClickedItem.tcrp;
        url = `/views/analysis/program-history/${this.system.id}`;
      }
      window.open(`${url}?${new URLSearchParams(params).toString()}`, '_blank');
    },
    onResize() {
      this.height = `${window.innerHeight - 182}px`;
    },
  },
  async created() {
    const rspn = await api.get('/prefer/prop/get-by-key?key=TopExeModules');
    if (isEmpty(rspn.data.value)) {
      // console.log('CONF_PROP [TopExeModules] is not existed.');
      return;
    }
    this.modules = rspn.data.value.split(',');
    this.modules.forEach(m => {
      /*
       * items/length/paging/loading의 속성이 modules의 값에 따라 동적으로 생성되므로
       * 반응성을 확보하기 위해서는 반드시 vm.$set()을 이용하여 update해주어야 한다.
       */
      this.$set(this.items, m, []);
      this.$set(this.length, m, 0);
      this.$set(this.paging, m, { page: 1, rowsPerPage: 20 });
      /*
       * paging의 내부 속성이 동적으로 생성되며 paging의 deep watch를 해야하는데
       * deep watch를 하게 되면 watcher()에서 value와 oldValue가 동일한 객체를 참조하게 된다.
       * 따라서, oldValue를 따로 보관해두어야 변경 여부를 체크할 수 있다.
       */
      this.oldPaging = merge({}, this.paging);
      this.$set(this.loading, m, false);
    });
  },
};
</script>

<style lang="scss" scoped>
.text-full-width {
  width: 190px;
  margin-bottom: 3px;
}
.v-list-item {
  min-height: 28px;
}
.top-title {
  width: 300px;
  padding: 2px 0px 10px 20px;
}
</style>
